//////////////////////////////////////
/* 	/src/lib/page-builder-lib.ts		*/
//////////////////////////////////////

/**
 * Escapes HTML to prevent XSS or tag injection.
 * Use a robust sanitizer for production if supporting user HTML.
 */
export function escapeHTML(str: string): string {
	return String(str)
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");
}

/**
 * Cleans and formats a numeric dimension string to a standardized CSS value,
 * ensuring up to three decimal places of precision (e.g., "1rem", "1.125rem").
 * Strips invalid characters, trims whitespace, and normalizes decimal precision.
 *
 * @param {string} numStr - The input string representing a CSS dimension (e.g., "1.2rem", " 1.23456rem ").
 * @returns {string} The cleaned, formatted dimension string with up to three decimals.
 *
 * @example
 * cleanDimenValues("1.23456rem"); // "1.235rem"
 * cleanDimenValues(" 2.1rem ");   // "2.1rem"
 */
export function cleanDimenValues(numStr: string) {
	// Remove trailing zeroes and possible decimal point if it's an integer
	const fixed = Number(numStr).toFixed(3);

	if (fixed.indexOf(".") === -1) return fixed;

	return fixed.replace(/\.?0+$/, "");
}

/**
 * Generates a random, alphanumeric string of the specified length for use as a unique
 * postfix or suffix (e.g., for custom CSS selectors, block IDs, or nested containers).
 * This eliminates the need for using indexes to identify nested content blocks.
 *
 * @param {number} [length=8] - The desired length of the unique key. Defaults to 8 characters.
 * @returns {string} A random, unique alphanumeric string of the specified length.
 *
 * @example
 * const key = generateUniqueKey();      // "f8d2b7e1"
 * const longKey = generateUniqueKey(12); // "f4e7c1d3a5b9"
 *
 * // Use in a CSS selector:
 * const selector = `.custom-block-${generateUniqueKey()}`;
 */
export function generateUniqueKey(length: number = 8): string {
	const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
	let result = "";
	for (let i = 0; i < length; i++) {
		result += chars.charAt(Math.floor(Math.random() * chars.length));
	}

	return result;
}


export function extractCustomFieldsFromHTML(html: string, knownTokens: string[]): string[] {
  const found = new Set<string>();
  if (!html) return [];

  // 1) Parse and collect spans
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const spans = doc.querySelectorAll('span[data-custom-field="true"]');
    spans.forEach((s) => {
      const v = s.getAttribute("data-field-value");
      if (v) found.add(v);
    });
  } catch {
    // ignore parse errors; we still do token scan below
  }

  // 2) Also check for raw tokens (older content that pre-dates spans)
  for (const token of knownTokens) {
    if (html.includes(token)) found.add(token);
  }

  return [...found];
}